<template>
  <svg
    :viewBox="viewBox"
    :width="svgWidth * svgScale"
    :height="svgHeight * svgScale"
  >
    <defs>
      <pattern
        id="holes_bg"
        width="5"
        height="5"
        patternUnits="userSpaceOnUse"
        patternContentUnits="userSpaceOnUse"
      >
        <rect
          x="0"
          y="0"
          :width="rackWidth"
          :height="rackWidth"
          fill="#071179"
        />
        <rect
          x="2.3"
          y="3.85"
          width="1.15"
          height="1.15"
          fill="white"
        />
      </pattern>
      <pattern
        id="holes"
        width="5"
        height="5"
        patternUnits="userSpaceOnUse"
        patternContentUnits="userSpaceOnUse"
      >
        <rect
          x="2.3"
          y="3.85"
          width="1.15"
          height="1.15"
          fill="white"
        />
      </pattern>
    </defs>
    <g
      :is="el.type"
      v-for="(el, i) in stack"
      :key="i"
      v-bind="el.props"
    />
  </svg>
</template>

<script>
  import Link from './shelving/Link';
  import Rack from './shelving/Rack';
  import Shelf from './shelving/Shelf';
  import Separator from './shelving/Separator';
  import Strips from './shelving/Strips';
  import Walls from './shelving/Walls';
  import Screens from './shelving/Screens';
  import Screw from './shelving/Screw';
  import Thruster from './shelving/Thruster';

  export default {
    name: 'ShelvingSVG',
    components: {
      Link,
      Rack,
      Shelf,
      Separator,
      Strips,
      Walls,
      Screens,
      Screw,
      Thruster,
    },
    props: {
      separate: {
        type: Boolean,
        default: true,
      },
      thrusters: {
        type: String,
        default: 'plastic',
      },
      rowAccessories: {
        type: Object,
        default: () => ({}),
      },
      sections: {
        type: Array,
        default: () => [],
      },
      rackWidth: {
        type: Number,
        default: 5.6,
      },
      space: {
        type: Number,
        default: 1,
      },
      depth: {
        type: Number,
        default: 40,
      },
      height: {
        type: Number,
        default: 200,
      },
      svgScale: {
        type: Number,
        default: 1,
      },
      scale: {
        type: Number,
        default: 1,
      },
    },
    computed: {
      viewBox() {
        return `0 0 ${this.svgWidth} ${this.svgHeight}`;
      },
      svgWidth() {
        return this.sectionsWidth + this.depthOffset.x;
      },
      svgHeight() {
        return this.scaledHeight + this.depthOffset.y + 11;
      },
      scaledHeight() {
        return this.height * this.scale;
      },
      sectionsWidth() {
        return this.sections.reduce((a, c) => a + c.width * this.scale, 0) + (this.sections.length - 1) * this.space;
      },
      rackOffset() {
        return {
          x: this.rackWidth / 1.8,
          y: this.rackWidth / 3,
          width: this.rackWidth,
          height: 2.4,
        };
      },
      depthOffset() {
        return {
          x: this.depth / 10 / 1.8,
          y: this.depth / 10 / 3,
        };
      },
      stack() {
        const stack = [];

        const addScrew = (props) => {
          stack.push({
            type: 'Screw',
            props: {
              ...props,
              x: props.x,
              y: props.y + this.scaledHeight,
              rackWidth: this.rackWidth,
            },
          });
        };

        const addThruster = (props) => {
          stack.push({
            type: 'Thruster',
            props: {
              ...props,
              x: props.x,
              y: props.y + this.scaledHeight,
              rackWidth: this.rackWidth,
            },
          });
        };

        const addRack = (props) => {
          if (props.back || props.front) {
            if (this.thrusters === 'adjustable') {
              addScrew({
                x: props.x,
                y: props.y,
              });
            }

            addThruster({
              x: props.x,
              y: props.y,
              left: props.left,
              right: props.right,
              front: props.front,
              back: props.back,
            });
          }

          stack.push({
            type: 'Rack',
            props: {
              width: this.rackWidth,
              height: this.scaledHeight,
              ...props,
            },
          });
        };

        const addSeparator = (x, y, height = 30) => {
          stack.push({
            type: 'Separator',
            props: {
              x,
              y,
              height,
              depthOffset: this.depthOffset,
            },
          });
        };

        const addStrips = (props) => {
          stack.push({
            type: 'Strips',
            props: {
              depthOffset: this.depthOffset,
              ...props,
            },
          });
        };

        const addWalls = (props) => {
          stack.push({
            type: 'Walls',
            props: {
              depthOffset: this.depthOffset,
              ...props,
            },
          });
        };

        const addScreens = (props) => {
          stack.push({
            type: 'Screens',
            props: {
              depthOffset: this.depthOffset,
              ...props,
            },
          });
        };

        const addShelf = (props) => {
          stack.push({
            type: 'Shelf',
            props: {
              depthOffset: this.depthOffset,
              plywood: props.parts === 0,
              ...props,
            },
          });
        };

        const addShelfs = ({x, width, shelfCount, parts, accessories}) => {
          const separatorsCount = (accessories.separators ? accessories.separators_count : 0);
          const sepatatorsPerShelf = Math.floor(separatorsCount / (shelfCount - 1));
          const sepatatorsLast = separatorsCount - sepatatorsPerShelf * (shelfCount - 2);
          const padTop = 0;
          const padBottom = 28;
          const shelfsHeight = this.scaledHeight - padTop - padBottom;
          const stepY = shelfsHeight / (shelfCount - 1);

          for (let i = shelfCount - 1; i >= 0; i--) {
            const isTop = (i === 0);
            const isSecond = (i === 1);
            const isBottom = (i === shelfCount - 1);
            const y = this.depthOffset.y + padTop + stepY * i;

            addShelf({x, y, width, parts});

            if (!isTop) {
              if (accessories.back_screen && shelfCount - accessories.back_screen_count <= i) {
                addScreens({
                  x,
                  y,
                  width,
                  back: true,
                });
              }

              if (accessories.side_screen && accessories.side_screen_left_count >= i) {
                addScreens({
                  x,
                  y,
                  width,
                  left: true,
                  right: false,
                });
              }

              if (accessories.side_screen && accessories.side_screen_right_count >= i) {
                addScreens({
                  x,
                  y,
                  width,
                  left: false,
                  right: true,
                });
              }

              if (accessories.back_strips && shelfCount - accessories.back_strips_count <= i) {
                addStrips({
                  x,
                  y: y - 30,
                  width,
                  back: true,
                });
              }

              if (accessories.side_strips_left && accessories.side_strips_left_count >= i) {
                addStrips({
                  x,
                  y: y - 30,
                  width,
                  left: true,
                });
              }

              if (accessories.side_strips_right && accessories.side_strips_right_count >= i) {
                addStrips({
                  x,
                  y: y - 30,
                  width,
                  right: true,
                });
              }

              const count = (isSecond ? sepatatorsLast : sepatatorsPerShelf);
              const space = width / (count + 1);
              for (let j = 1; j <= count; j++) {
                addSeparator(x + space * j, y, 30);
              }
            }
          }
        };

        let offsetX = 0;
        const first_count = this.sections[0].count - 1;
        const last_count = this.sections[this.sections.length - 1].count - 1;
        const screen_count = +this.rowAccessories.side_screen_count;
        const row_side_screen_left_count = screen_count + (last_count < screen_count ? screen_count - last_count : 0);
        const row_side_screen_right_count = screen_count + (first_count < screen_count ? screen_count - first_count : 0);

        for (let i in this.sections) {
          const section = this.sections[i];
          const isFirst = (i == 0);
          const isLast = (i == this.sections.length - 1);
          const hasLeftRacks = (isFirst || this.separate);
          const scaledWidth = section.width * this.scale;
          const rightRackOffsetX = offsetX + scaledWidth - this.rackWidth;
          const accessories = {};

          if (accessories.back_wall || accessories.side_wall_left) {
            addWalls({
              x: offsetX,
              y: this.depthOffset.y,
              width: scaledWidth,
              height: this.scaledHeight - 28,
              back: accessories.back_wall,
              left: accessories.side_wall_left,
            });
          }

          if (hasLeftRacks) {
            addRack({
                x: offsetX + this.depthOffset.x - this.rackOffset.x,
                y: this.rackOffset.y,
                left: true,
                back: true,
            });

            addRack({
              x: offsetX,
              y: this.depthOffset.y,
              left: true,
            });
          }

          addRack({
            x: rightRackOffsetX + this.depthOffset.x - this.rackOffset.x,
            y: this.rackOffset.y,
            back: true,
          });

          addShelfs({
            x: offsetX,
            width: scaledWidth,
            shelfCount: section.count,
            parts: section.parts,
            accessories,
          });

          if (hasLeftRacks) {
            addRack({
              x: offsetX,
              y: this.depthOffset.y,
              front: true,
            });
          }

          addRack({
            x: rightRackOffsetX + this.depthOffset.x - this.rackOffset.x,
            y: this.rackOffset.y,
            right: true,
          });

          addRack({
            x: rightRackOffsetX,
            y: this.depthOffset.y,
            right: true,
            front: true,
          });

          if (accessories.side_wall_right) {
            addWalls({
              x: offsetX,
              y: this.depthOffset.y,
              width: scaledWidth,
              height: this.scaledHeight - 28,
              right: accessories.side_wall_right,
            });
          }

          offsetX += scaledWidth;
          offsetX += this.space;
        }

        return stack;
      },
    },
  }
</script>

<style>

</style>
